// ---------------------------------------------------------------------------
// Lanczos approximation for log-gamma
// ---------------------------------------------------------------------------

const LANCZOS_G = 7;
const LANCZOS_COEFFICIENTS = [
  0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313, -176.61502916214059,
  12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
];

export function lnGamma(z: number): number {
  if (z < 0.5) {
    // Reflection formula: Γ(z)·Γ(1−z) = π / sin(πz)
    return Math.log(Math.PI / Math.sin(Math.PI * z)) - lnGamma(1 - z);
  }

  z -= 1;
  let x = LANCZOS_COEFFICIENTS[0];
  for (let i = 1; i < LANCZOS_G + 2; i++) {
    x += LANCZOS_COEFFICIENTS[i] / (z + i);
  }

  const t = z + LANCZOS_G + 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(x);
}

export function gamma(z: number): number {
  return Math.exp(lnGamma(z));
}

// ---------------------------------------------------------------------------
// Regularized incomplete beta function I_x(a, b)
// Uses continued fraction (Lentz's method)
// ---------------------------------------------------------------------------

function betaCf(x: number, a: number, b: number): number {
  const maxIter = 200;
  const eps = 1e-14;
  const tiny = 1e-30;

  const qab = a + b;
  const qap = a + 1;
  const qam = a - 1;

  let c = 1;
  let d = 1 - (qab * x) / qap;
  if (Math.abs(d) < tiny) {
    d = tiny;
  }
  d = 1 / d;
  let h = d;

  for (let m = 1; m <= maxIter; m++) {
    const m2 = 2 * m;

    // Even step
    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < tiny) {
      d = tiny;
    }
    c = 1 + aa / c;
    if (Math.abs(c) < tiny) {
      c = tiny;
    }
    d = 1 / d;
    h *= d * c;

    // Odd step
    aa = -((a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < tiny) {
      d = tiny;
    }
    c = 1 + aa / c;
    if (Math.abs(c) < tiny) {
      c = tiny;
    }
    d = 1 / d;
    const del = d * c;
    h *= del;

    if (Math.abs(del - 1) < eps) {
      break;
    }
  }

  return h;
}

export function regularizedBeta(x: number, a: number, b: number): number {
  if (x <= 0) {
    return 0;
  }
  if (x >= 1) {
    return 1;
  }

  const logBeta = lnGamma(a) + lnGamma(b) - lnGamma(a + b);
  const front = Math.exp(Math.log(x) * a + Math.log(1 - x) * b - logBeta);

  if (x < (a + 1) / (a + b + 2)) {
    return (front * betaCf(x, a, b)) / a;
  }
  return 1 - (front * betaCf(1 - x, b, a)) / b;
}

// ---------------------------------------------------------------------------
// Regularized lower incomplete gamma P(s, x)
// ---------------------------------------------------------------------------

function gammaPSeries(s: number, x: number): number {
  const maxIter = 200;
  const eps = 1e-14;

  let ap = s;
  let sum = 1 / s;
  let del = 1 / s;

  for (let i = 0; i < maxIter; i++) {
    ap += 1;
    del *= x / ap;
    sum += del;
    if (Math.abs(del) < Math.abs(sum) * eps) {
      break;
    }
  }

  return sum * Math.exp(-x + s * Math.log(x) - lnGamma(s));
}

function gammaPCf(s: number, x: number): number {
  const maxIter = 200;
  const eps = 1e-14;
  const tiny = 1e-30;

  let b = x + 1 - s;
  let c = 1 / tiny;
  let d = 1 / b;
  let h = d;

  for (let i = 1; i <= maxIter; i++) {
    const an = -i * (i - s);
    b += 2;
    d = an * d + b;
    if (Math.abs(d) < tiny) {
      d = tiny;
    }
    c = b + an / c;
    if (Math.abs(c) < tiny) {
      c = tiny;
    }
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < eps) {
      break;
    }
  }

  return 1 - Math.exp(-x + s * Math.log(x) - lnGamma(s)) * h;
}

export function regularizedGammaP(s: number, x: number): number {
  if (x <= 0) {
    return 0;
  }
  if (x < s + 1) {
    return gammaPSeries(s, x);
  }
  return gammaPCf(s, x);
}

// ---------------------------------------------------------------------------
// t-distribution
// ---------------------------------------------------------------------------

export function tPdf(t: number, df: number): number {
  const halfDfPlus1 = (df + 1) / 2;
  const halfDf = df / 2;
  return (
    Math.exp(lnGamma(halfDfPlus1) - lnGamma(halfDf)) /
    (Math.sqrt(df * Math.PI) * Math.pow(1 + (t * t) / df, halfDfPlus1))
  );
}

export function tCdf(t: number, df: number): number {
  const x = df / (df + t * t);
  const ib = regularizedBeta(x, df / 2, 0.5);
  if (t >= 0) {
    return 1 - 0.5 * ib;
  }
  return 0.5 * ib;
}

// ---------------------------------------------------------------------------
// Chi-squared distribution
// ---------------------------------------------------------------------------

export function chiSquaredPdf(x: number, k: number): number {
  if (x <= 0) {
    return 0;
  }
  const halfK = k / 2;
  return Math.exp((halfK - 1) * Math.log(x) - x / 2 - halfK * Math.log(2) - lnGamma(halfK));
}

export function chiSquaredCdf(x: number, k: number): number {
  if (x <= 0) {
    return 0;
  }
  return regularizedGammaP(k / 2, x / 2);
}

// ---------------------------------------------------------------------------
// Binomial distribution
// ---------------------------------------------------------------------------

function binomialPmf(k: number, n: number, p: number): number {
  if (k < 0 || k > n) {
    return 0;
  }
  const logCoeff = lnGamma(n + 1) - lnGamma(k + 1) - lnGamma(n - k + 1);
  return Math.exp(logCoeff + k * Math.log(p) + (n - k) * Math.log(1 - p));
}

export function binomialCdf(k: number, n: number, p: number): number {
  if (k < 0) {
    return 0;
  }
  if (k >= n) {
    return 1;
  }
  // Use regularized incomplete beta: P(X <= k) = I_{1-p}(n-k, k+1)
  return regularizedBeta(1 - p, n - k, k + 1);
}

export { binomialPmf };

// ---------------------------------------------------------------------------
// Inverse CDF via bisection
// ---------------------------------------------------------------------------

export function tInvCdf(p: number, df: number): number {
  if (p <= 0) {
    return -Infinity;
  }
  if (p >= 1) {
    return Infinity;
  }
  if (p === 0.5) {
    return 0;
  }

  let lo = -50;
  let hi = 50;
  for (let i = 0; i < 100; i++) {
    const mid = (lo + hi) / 2;
    if (tCdf(mid, df) < p) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return (lo + hi) / 2;
}

export function chiSquaredInvCdf(p: number, k: number): number {
  if (p <= 0) {
    return 0;
  }
  if (p >= 1) {
    return Infinity;
  }

  let lo = 0;
  let hi = Math.max(k * 4, 40);
  // Expand upper bound if needed
  while (chiSquaredCdf(hi, k) < p) {
    hi *= 2;
  }

  for (let i = 0; i < 100; i++) {
    const mid = (lo + hi) / 2;
    if (chiSquaredCdf(mid, k) < p) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return (lo + hi) / 2;
}
